import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys, type ProjectOverview, type ProjectSummary } from "./hooks";

/**
 * Project soft archive and audited restore (#440, over #434's service half).
 *
 * Aliases into the generated contract, the `hooks.ts` rule: change the
 * contract, regenerate, and these names either still resolve or fail to
 * compile.
 */
export type ProjectArchiveStatus = components["schemas"]["ProjectArchiveStatus"];
export type ProjectArchiveRefusalReason =
  components["schemas"]["ProjectArchiveRefusalReason"];
export type ArchiveProjectRequest = components["schemas"]["ArchiveProjectRequest"];

/**
 * Archive or restore, compare-and-set (#434's contract).
 *
 * ONE private hook parameterised by path, two thin exported wrappers — the
 * `useLifecycleMutation` shape from `library.ts`, with the retarget's CAS
 * rule on top: `expectedArchiveRevision` travels WITH the request from the
 * caller, never re-read inside the hook, because the value that must be sent
 * is the one the surface DISPLAYED when the clinician confirmed.
 *
 * `onSuccess` does both halves of what the retarget does only one of, and the
 * asymmetry is the point: the returned `ProjectSummary` is written into the
 * overview cache (the displayed archive revision is bytes the service sent,
 * never local arithmetic), AND the `["projects"]` prefix is invalidated —
 * unlike a retarget, an archive changes list MEMBERSHIP, so the index
 * genuinely must re-read. The prefix reaches every `search`/`includeArchived`
 * variant and every per-project subtree, which for a lifecycle change is the
 * cheapest correct thing (`library.ts` invalidates its own root for the same
 * reason).
 *
 * `onError` reacts to both stale CAS and ambiguous failures by invalidating
 * the same set. A stale refusal is news about the resource, not just about
 * this request (`useReplaceProjectAdoptionsMutation`'s rule); an interrupted
 * response is ambiguous because the service may have committed before the
 * browser lost the answer. In both cases the contract's recovery is to read
 * first, not to claim nothing changed or re-offer the old revision blind.
 * The invalidation is awaited so every active project query finishes its
 * refresh attempt before `mutateAsync` rejects back to the dialog; a failed
 * re-read is still rendered by that query's existing stale-data handling.
 *
 * `retry: false`, like every mutation here: the 409s mean "somebody decided
 * first" or "the Project's own state refuses", and no automatic retry answers
 * either.
 */
function useProjectLifecycleMutation(
  projectId: string,
  path:
    | "/v1/projects/{projectId}/archive"
    | "/v1/projects/{projectId}/restore",
): UseMutationResult<ProjectSummary, unknown, ArchiveProjectRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: ArchiveProjectRequest) {
      return unwrap(
        await client.POST(path, {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(summary) {
      queryClient.setQueryData<ProjectOverview>(
        queryKeys.projectOverview(projectId),
        (current) => (current ? { ...current, project: summary } : current),
      );
      // Awaited, the adoptions rule: the confirmation dialog closes when
      // `mutateAsync` resolves, and closing it over an index that still
      // lists the archived Project would announce a completion the visible
      // page contradicts.
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    async onError(error) {
      const failure = classifyProjectArchiveFailure(error);
      if (failure.kind === "stale" || failure.kind === "ambiguous") {
        await queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
    },
    retry: false,
  });
}

/** Archive: soft, evidence-preserving, never deletion (#434's contract). */
export function useArchiveProjectMutation(
  projectId: string,
): UseMutationResult<ProjectSummary, unknown, ArchiveProjectRequest> {
  return useProjectLifecycleMutation(
    projectId,
    "/v1/projects/{projectId}/archive",
  );
}

/** Restore: the audited admin/operations counterpart. */
export function useRestoreProjectMutation(
  projectId: string,
): UseMutationResult<ProjectSummary, unknown, ArchiveProjectRequest> {
  return useProjectLifecycleMutation(
    projectId,
    "/v1/projects/{projectId}/restore",
  );
}

/**
 * Why an archive or restore was refused.
 *
 * Kinds only, the `RetargetFailure` convention: the sentences live with the
 * surface that renders them, keyed by these members with `satisfies`, so a
 * new kind fails the build rather than rendering blank.
 *
 * `stale` carries the revision the service holds — read from the envelope
 * with the same `undefined`-tolerant guard the retarget uses, because the
 * envelope is data. `refused` carries the stable reason from the contract's
 * OWN field for this resource (`projectArchiveRefusalReason`, deliberately
 * not the library's `archiveRefusalReason` — the contract split them so the
 * generated type is never a guess, and this module mirrors the split).
 */
export type ProjectArchiveFailure =
  /** 409 `stale_archive_revision` — somebody changed the archive state first. */
  | { kind: "stale"; currentRevision: number | undefined }
  /** 409 `project_archive_refused` — the Project's own state refuses, with its reason. */
  | { kind: "refused"; reason: ProjectArchiveRefusalReason | (string & {}) }
  | { kind: "denied" }
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /**
   * No trustworthy answer came back. The write may have committed before the
   * response was interrupted, so the caller must re-read before deciding.
   */
  | { kind: "ambiguous"; code: string; requestId: string | undefined }
  /** A definite non-success answer, carrying no unclassified server prose. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifyProjectArchiveFailure(
  error: unknown,
): ProjectArchiveFailure {
  if (error instanceof ApiError) {
    if (error.code === "stale_archive_revision") {
      const current = error.envelope["currentArchiveRevision"];
      return {
        kind: "stale",
        currentRevision: typeof current === "number" ? current : undefined,
      };
    }
    if (error.code === "project_archive_refused") {
      const reason = error.envelope["projectArchiveRefusalReason"];
      return {
        kind: "refused",
        reason: typeof reason === "string" && reason.length > 0 ? reason : "",
      };
    }
    // Project scope deliberately makes absent and unauthorized identical.
    // A mutation against a row that was already displayed cannot turn a 404
    // into "the service could not be reached" without defeating that fold.
    if (
      error.isForbidden ||
      error.isUnauthenticated ||
      error.status === 404
    ) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    // A 5xx can be produced after the transaction committed but before a
    // usable response reached the browser. Like a transport exception below,
    // it cannot support either "succeeded" or "nothing changed".
    if (error.status >= 500) {
      return {
        kind: "ambiguous",
        code: error.code,
        requestId: error.requestId,
      };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network/runtime interruption. The request may
  // have reached the service, so its outcome is unknown rather than failed.
  return {
    kind: "ambiguous",
    code: "upstream_unavailable",
    requestId: undefined,
  };
}
